
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { useToast } from '@/hooks/use-toast';
import Sidebar from './Sidebar';
import Header from './Header';

type DashboardLayoutProps = {
  children: React.ReactNode;
};

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userInitials, setUserInitials] = useState('');
  const navigate = useNavigate();
  const supabase = useSupabaseClient();
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/auth');
          return;
        }
        
        if (user.user_metadata?.name) {
          setUserName(user.user_metadata.name);
          
          // Generate initials from name
          const nameParts = user.user_metadata.name.split(' ');
          if (nameParts.length > 1) {
            setUserInitials(`${nameParts[0][0]}${nameParts[1][0]}`);
          } else if (nameParts.length === 1) {
            setUserInitials(nameParts[0].substring(0, 2));
          }
        } else {
          // Fallback to email
          setUserInitials(user.email?.substring(0, 2).toUpperCase() || 'SU');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, [navigate, supabase]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Utloggad",
        description: "Du har loggats ut från ditt konto."
      });
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Fel vid utloggning",
        description: "Ett fel uppstod vid utloggning. Försök igen.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar 
        isSidebarOpen={isSidebarOpen} 
        setIsSidebarOpen={setIsSidebarOpen} 
        userName={userName} 
        userInitials={userInitials} 
        onSignOut={handleSignOut} 
      />

      <div className="flex flex-col flex-1">
        <Header setIsSidebarOpen={setIsSidebarOpen} />

        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
